import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "332",
  "slug": "largest-waterfalls-in-the-world-ranked",
  "title": "The Largest Waterfalls in the World, Ranked by Volume (Not Height)",
  "excerpt": "The tallest waterfalls barely move any water. Here are the planet's true heavyweights — the wide, thundering cataracts that push the most water over the edge every second.",
  "date": "2026-06-03",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1527588410398-46e8ac242824?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx3YXRlcmZhbGwlMjBhZXJpYWx8ZW58MXwwfHx8MTc4MDQ3NjkyMnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "By sheer water volume, the biggest waterfalls aren't the famous tall ones like Angel Falls. The real heavyweights are wide tropical-river cataracts — Inga and Boyoma on the Congo and Khone Phapheng on the Mekong — that move tens of thousands of cubic metres of water every second.",
  "content": [
    {
      "type": "paragraph",
      "text": "Ask someone to name the world's biggest waterfall and they'll usually reach for the tallest one they can think of. But height and size are two very different things. A waterfall can drop nearly a kilometre and carry barely more water than a garden hose, while a 20-metre cataract on a major river can move more water in a second than a city uses in an hour. If you rank by volume — the amount of water actually going over the edge — the list looks nothing like the postcards."
    },
    {
      "type": "heading",
      "text": "Height Is the Wrong Way to Measure a Waterfall"
    },
    {
      "type": "paragraph",
      "text": "Angel Falls in Venezuela plunges around 979 metres, the tallest uninterrupted drop on Earth. It's spectacular, but it forms on a small river high on a tabletop mountain, and in the dry season the water can scatter into mist before it ever reaches the bottom. Volume is a completely different measurement: width times depth times speed of flow. The waterfalls that win on volume tend to be short and absurdly wide, sitting on the planet's largest rivers."
    },
    {
      "type": "paragraph",
      "text": "One more thing to keep in mind: these are averages. A waterfall's flow can multiply several times over between the dry season and the flood season, which is exactly why you'll see different numbers in different sources."
    },
    {
      "type": "heading",
      "text": "The Heavyweights Almost Nobody Has Heard Of"
    },
    {
      "type": "paragraph",
      "text": "Ranked roughly by average flow, the most powerful falls on Earth are names most people have never encountered:"
    },
    {
      "type": "list",
      "items": [
        "Inga Falls, Democratic Republic of the Congo — the Congo River tumbles through a stretch of rapids that average tens of thousands of cubic metres per second, more water than any other falls on the planet. Some geographers count it as rapids rather than a single waterfall, which is part of why it rarely tops popular lists.",
        "Boyoma Falls, DR Congo — seven cataracts on the upper Congo, commonly cited at around 17,000 cubic metres per second and frequently named the largest true waterfall by volume.",
        "Khone Phapheng Falls, Laos — here the Mekong fans out across roughly ten kilometres of channels and islands. It averages on the order of 11,000 cubic metres per second and is the widest waterfall in Southeast Asia.",
        "Guaira Falls (Sete Quedas), Brazil and Paraguay — once one of the most powerful waterfalls on Earth, drowned in 1982 beneath the reservoir of the Itaipu Dam. It's a reminder that even giants can vanish."
      ]
    },
    {
      "type": "heading",
      "text": "The Famous Ones, in Context"
    },
    {
      "type": "paragraph",
      "text": "The waterfalls everyone actually knows are smaller than the Congo and Mekong monsters, but they're still enormous — and far more accessible."
    },
    {
      "type": "list",
      "items": [
        "Niagara Falls, USA and Canada — averages around 2,400 cubic metres per second, the benchmark most people picture when they imagine a big waterfall.",
        "Iguazu Falls, Argentina and Brazil — a horseshoe of roughly 275 separate drops with average flow comfortably over a thousand cubic metres per second.",
        "Victoria Falls, Zambia and Zimbabwe — often called the largest single sheet of falling water, measuring width times height. Its flow swings dramatically between a trickle-thin dry season and a thundering wall in flood."
      ]
    },
    {
      "type": "heading",
      "text": "And the Tallest? A Quick Honour Roll"
    },
    {
      "type": "paragraph",
      "text": "Since most people arrive at this question thinking about height, here's the other leaderboard. Angel Falls holds the record for the highest uninterrupted drop at around 979 metres, pouring off the edge of a flat-topped tepui in Venezuela's remote highlands. Tugela Falls in South Africa's Drakensberg is its closest rival and is sometimes measured as taller overall once its multiple tiers are added together. What unites the world's tallest falls is that they almost all sit on small rivers high in the mountains — which is precisely why none of them comes close on volume. Height and power simply live in different places."
    },
    {
      "type": "heading",
      "text": "Why the Numbers Are Always Disputed"
    },
    {
      "type": "paragraph",
      "text": "There's no official referee for waterfall size. The big disagreements come down to three things: whether a feature counts as a single waterfall or a series of rapids, how much the flow varies through the year, and how carefully anyone has actually measured a remote cataract deep in the Congo basin. So treat any ranked list — including this one — as a good approximation rather than a settled scoreboard."
    },
    {
      "type": "paragraph",
      "text": "It also helps to remember that a waterfall is a moving target in a way a mountain isn't. Many of the world's biggest freeze partly solid in winter, swell to several times their normal flow in the wet season, and slowly grind their way upstream over centuries. Niagara has retreated more than ten kilometres up its gorge since the last ice age. When you measure a waterfall, you're really capturing one frame of something that never stops changing."
    },
    {
      "type": "heading",
      "text": "How to See a Big Waterfall From Space"
    },
    {
      "type": "paragraph",
      "text": "The biggest cataracts often leave clues you can read from a satellite view. Look for a sharp bright line cutting straight across a wide river, with the water below churned a paler, sediment-stirred colour and sometimes a faint plume of spray. Above a major falls there's frequently a reservoir or a sudden widening of the river; below it, a deep gorge the falling water has carved over thousands of years. Spotting that signature — a river that abruptly steps down and changes texture — is exactly the kind of detail that separates a good guess from a lucky one."
    },
    {
      "type": "paragraph",
      "text": "Keep height, width, and flow separate when comparing waterfalls. A ranking by drop height answers a different question from a ranking by water volume, and flow varies with the season."
    }
  ]
};
